/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { fetchAllAirtableRecords } from "./client";
import { normalizeIndustries } from "./industry-map";
import { normalizeWebsiteUrl } from "./url";

import type { AirtableRecord } from "./client";
import type { TrainingPartner } from "./types";

const BASE_ID = "appv9yj7tx8PG3jdK";
const TABLE_NAME = "MM-Directory-DON'T DELETE";
// Show only rows the editor has marked production-ready AND that have the
// public-listing consent checked. Either falling → hidden.
const PUBLIC_FILTER = `AND({Ready for Prod}=TRUE(), {Consent for Public Listing}=TRUE())`;

interface AirtableAttachment {
	id: string;
	width?: number;
	height?: number;
	url: string;
	filename?: string;
	thumbnails?: {
		small?: { url: string };
		large?: { url: string };
		full?: { url: string };
	};
}

interface TrainingPartnerFields {
	"MM-Center Display Name"?: string;
	"MM-Training Contact Email"?: string;
	"MM-Industry"?: string[];
	"MM-Country"?: string[];
	"MM-Language"?: string[];
	"MM-Logo"?: AirtableAttachment[];
	"Link to Badge Level"?: string[];
	"Link to UATC Webpage"?: string[];
	"MM-Course Site"?: string;
	"MM-Description"?: string;
}

function mapRecord(record: AirtableRecord<TrainingPartnerFields>): TrainingPartner | null {
	const f = record.fields;
	const name = f["MM-Center Display Name"];
	// Skip rows without a display name — those are incomplete entries.
	if (!name) return null;

	// Airtable attachment URLs are signed and expire ~2h after issue. Only the
	// `large` thumbnail (Airtable caps it ≈ 512px) is a stable resized
	// derivative — fall through to a no-logo card if Airtable hasn't
	// generated one yet, rather than handing the full-resolution `url`
	// (which can be a 1500+px PNG) to the browser.
	//
	// Note: cards display at 80×80 so `large` is ~3× larger than the rendered
	// pixel size. `<img width=80 height=80 loading="lazy" decoding="async">`
	// in the card bounds the layout and decode cost; the byte cost is
	// acceptable until/unless we add a CDN-sized loader.
	const logoAttachment = f["MM-Logo"]?.[0];
	const logoUrl = logoAttachment?.thumbnails?.large?.url;

	// `Link to UATC Webpage` is a multipleLookupValues field — Airtable returns
	// an array even when a single linked record is selected. Use the first.
	const rawWebsiteUrl = f["Link to UATC Webpage"]?.[0] ?? f["MM-Course Site"];
	// Normalize to a scheme-qualified absolute URL — Airtable text fields
	// commonly contain bare hosts like "partner.com" which the renderer would
	// otherwise resolve as a same-origin link.
	const websiteUrl = normalizeWebsiteUrl(rawWebsiteUrl);

	// Same for badge level (Bronze / Silver / Gold from the linked UATC record).
	const badgeLevel = f["Link to Badge Level"]?.[0];

	return {
		id: record.id,
		name,
		country: f["MM-Country"]?.[0] ?? "",
		// Canonicalize through the same industry map Instructors use so chip
		// labels are consistent across variants (a partner tagged "Game Dev"
		// folds into "Games" — same as the Instructor chip vocabulary).
		industries: normalizeIndustries(f["MM-Industry"]),
		languages: f["MM-Language"],
		logoUrl,
		websiteUrl,
		email: f["MM-Training Contact Email"],
		badgeLevel,
	};
}

/**
 * Fetch every public training center from the UE training-partner Airtable
 * `MM-Directory-DON'T DELETE` table.
 *
 * Filters to rows where both `Ready for Prod` AND `Consent for Public
 * Listing` are checked. Anything not yet approved by Partners ops + the
 * partner themselves is hidden.
 *
 * Requires `AIRTABLE_TRAINING_PARTNER_PAT` to be set; throws if missing
 * (fail-loud so vault misconfiguration is visible).
 */
export async function fetchAllTrainingPartners(): Promise<TrainingPartner[]> {
	const records = await fetchAllAirtableRecords<TrainingPartnerFields>({
		baseId: BASE_ID,
		tableName: TABLE_NAME,
		pat: process.env.AIRTABLE_TRAINING_PARTNER_PAT,
		filterByFormula: PUBLIC_FILTER,
		consumerLabel: "Training Partners",
		envVarName: "AIRTABLE_TRAINING_PARTNER_PAT",
	});

	return records.map(mapRecord).filter((p): p is TrainingPartner => p !== null);
}
